<script setup lang="ts">
import Footer from '@/components/Footer.vue';
import LicenseCardChoice, { type CardChoiceItem, type CardKind } from '@/components/LicenseCardChoice.vue';
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import { useAuthStore } from '@/stores/useAuthStore';
import { useThemeStore, THEMES, type Theme } from '@/stores/useThemeStore';
import dartsHubApi from '@/axios/dartsHubApi';
import ffdLogo from '@/assets/ffd.png';
import dartshubLogo from '@/assets/logo-bleu.png';
import html2canvas from 'html2canvas';
import { getCurrentSeason } from '@/config/config';
import { useConfirm } from '@/composables/useConfirm';

const authStore = useAuthStore();
const themeStore = useThemeStore();
const router = useRouter();
const route = useRoute();
const { open: openConfirm } = useConfirm();

const redirectTarget = route.query.redirect as string | undefined;
const redirectDomain = redirectTarget
  ? (() => { try { return new URL(redirectTarget).hostname; } catch { return null; } })()
  : null;

function goToRedirect() {
  if (redirectTarget) window.location.href = redirectTarget;
}

const editMode = ref(false);
const passwordMode = ref(false);
const uploading = ref(false);
const saving = ref(false);
const loadingProfile = ref(true);
const loadingLicenses = ref(true);

const categoryLabel: Record<string, string> = {
  M: 'Masculin', F: 'Féminine', JG: 'Junior Garçon', JF: 'Junior Fille',
};

interface License {
  id: number;
  license_number: number;
  season: string;
  category: string;
  gender: string;
  birth_date: string;
  club_id: number;
  club_number: number;
  club_name: string;
  ligue: string;
  comite: string;
  created_at: string;
  name: string;
  firstname: string;
  // Carte officielle FFD importée par le comité (cf. backend licenses/ffd_cards.py).
  ffd_card_uploaded_at?: string | null;
  // Fin de validité : renseignée une fois la saison finie ou la licence quittée par transfert.
  valid_until?: string | null;
  // Noms complets ("Sud-Est", "Méridional"), pour la carte générée.
  ligue_name?: string | null;
  comite_name?: string | null;
}

// Seuil vétéran FFD : 50 ans, calculé au 31 décembre de la 1ère année de la saison
// (même référence que le calcul de la catégorie junior côté backend, cf. _calculate_category).
const VETERAN_AGE = 50;

const formatCategory = (lic: License): string => {
  const base = categoryLabel[lic.category] || lic.category;
  if ((lic.category !== 'M' && lic.category !== 'F') || !lic.birth_date || !lic.season) return base;

  const startYear = parseInt(lic.season.split('-')[0], 10);
  const birthDate = new Date(lic.birth_date);
  if (!Number.isFinite(startYear) || Number.isNaN(birthDate.getTime())) return base;

  const refDate = new Date(startYear, 11, 31);
  let age = refDate.getFullYear() - birthDate.getFullYear();
  if (refDate.getMonth() < birthDate.getMonth() ||
    (refDate.getMonth() === birthDate.getMonth() && refDate.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= VETERAN_AGE ? `${base} Vétéran` : base;
};

const licenses = ref<License[]>([]);

interface Session {
  id: number;
  app_name: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  expires_at: string;
}

const sessions = ref<Session[]>([]);
const loadingSessions = ref(true);
const revokingId = ref<number | null>(null);
const revokingAll = ref(false);
const showAllSessions = ref(false);
const SESSIONS_PREVIEW_COUNT = 8;
const visibleSessions = computed(() =>
  showAllSessions.value ? sessions.value : sessions.value.slice(0, SESSIONS_PREVIEW_COUNT)
);

const siteLabel = (appName: string | null): string => {
  const a = (appName || '').toLowerCase();
  if (a.includes('cmer')) return 'CMER';
  if (a.includes('lsef')) return 'LSEF';
  if (a.includes('badarts')) return 'Badarts';
  if (a.includes('dartshub') || a === 'default') return 'DartsHub';
  if (a.startsWith('swagger_')) return 'API (docs)';
  return appName || 'Site inconnu';
};

const deviceLabel = (userAgent: string | null): string => {
  if (!userAgent) return 'Appareil inconnu';
  const ua = userAgent;
  let os = 'Appareil inconnu';
  if (/iphone/i.test(ua)) os = 'iPhone';
  else if (/ipad/i.test(ua)) os = 'iPad';
  else if (/android/i.test(ua)) os = 'Android';
  else if (/windows/i.test(ua)) os = 'Windows';
  else if (/mac os/i.test(ua)) os = 'Mac';
  else if (/linux/i.test(ua)) os = 'Linux';

  let browser = '';
  if (/edg\//i.test(ua)) browser = 'Edge';
  else if (/opr\/|opera/i.test(ua)) browser = 'Opera';
  else if (/chrome\//i.test(ua)) browser = 'Chrome';
  else if (/firefox\//i.test(ua)) browser = 'Firefox';
  else if (/safari\//i.test(ua)) browser = 'Safari';

  return browser ? `${browser} sur ${os}` : os;
};

const formatSessionDate = (iso: string): string => {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

interface PartnerConsent {
  client_id: string;
  client_name: string;
  granted_at: string;
}

const consents = ref<PartnerConsent[]>([]);
const loadingConsents = ref(true);
const revokingConsentId = ref<string | null>(null);

const loadConsents = async () => {
  loadingConsents.value = true;
  try {
    const { data } = await dartsHubApi.get('/partners/consent/mine');
    consents.value = data;
  } finally {
    loadingConsents.value = false;
  }
};

const revokeConsent = async (consent: PartnerConsent) => {
  const ok = await openConfirm(
    'Révoquer cette autorisation ?',
    `${consent.client_name} ne pourra plus vous identifier via DartsHub tant que vous ne l'autorisez pas à nouveau.`,
  );
  if (!ok) return;
  revokingConsentId.value = consent.client_id;
  try {
    await dartsHubApi.delete(`/partners/consent/${consent.client_id}`);
    consents.value = consents.value.filter((c) => c.client_id !== consent.client_id);
    toast.success('Autorisation révoquée.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la révocation.');
  } finally {
    revokingConsentId.value = null;
  }
};

interface ConnectedApp {
  key: string;
  name: string;
  type: 'session' | 'partner';
  count: number;
  lastDate: string;
  sessionIds: number[];
  consent?: PartnerConsent;
}

const connectedApps = computed<ConnectedApp[]>(() => {
  const byName = new Map<string, ConnectedApp>();
  for (const s of sessions.value) {
    const name = siteLabel(s.app_name);
    const app = byName.get(name);
    if (app) {
      app.sessionIds.push(s.id);
      app.count++;
      if (s.created_at > app.lastDate) app.lastDate = s.created_at;
    } else {
      byName.set(name, { key: `session:${name}`, name, type: 'session', count: 1, lastDate: s.created_at, sessionIds: [s.id] });
    }
  }
  const partnerApps: ConnectedApp[] = consents.value.map((c) => ({
    key: `partner:${c.client_id}`, name: c.client_name, type: 'partner', count: 0, lastDate: c.granted_at, sessionIds: [], consent: c,
  }));
  return [...byName.values(), ...partnerApps].sort((a, b) => b.lastDate.localeCompare(a.lastDate));
});

const revokingAppKey = ref<string | null>(null);

const disconnectApp = async (app: ConnectedApp) => {
  const ok = await openConfirm(
    'Déconnecter cette application ?',
    `Toutes les sessions actives de ${app.name} (${app.count} appareil${app.count > 1 ? 's' : ''}) seront déconnectées.`,
  );
  if (!ok) return;
  revokingAppKey.value = app.key;
  try {
    await Promise.all(app.sessionIds.map((id) => dartsHubApi.post(`/auth/sessions/${id}/revoke`)));
    const ids = new Set(app.sessionIds);
    sessions.value = sessions.value.filter((s) => !ids.has(s.id));
    toast.success(`${app.name} déconnecté.`);
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la déconnexion.');
  } finally {
    revokingAppKey.value = null;
  }
};

const form = ref({
  name: '',
  firstname: '',
  nickname: '',
  bio: '',
  city: '',
});

const passwordForm = ref({ next: '', confirm: '' });
const showPwNext = ref(false);
const showPwConfirm = ref(false);

const initials = computed(() => {
  const n = authStore.firstname?.[0] || '';
  const s = authStore.name?.[0] || '';
  return (n + s).toUpperCase() || '?';
});


// Plus récente d'abord. À saison égale (transfert en cours de saison : l'ancienne licence
// est close, valid_until renseigné), celle toujours valable passe devant.
const sortedLicenses = computed(() => [...licenses.value].sort((a, b) =>
  b.season.localeCompare(a.season)
  || (b.valid_until ?? '9999-12-31').localeCompare(a.valid_until ?? '9999-12-31')
  || b.id - a.id));
const lastLicense = computed(() => sortedLicenses.value[0] ?? null);

const isCurrentLicenseValid = computed(() =>
  lastLicense.value?.season === getCurrentSeason()
);

const loadProfile = async () => {
  loadingProfile.value = true;
  try {
    const { data } = await dartsHubApi.get('/users/me');
    form.value = {
      name: data.name || '',
      firstname: data.firstname || '',
      nickname: data.nickname || '',
      bio: data.bio || '',
      city: data.city || '',
    };
  } finally {
    loadingProfile.value = false;
  }
};

// Cartes de licence : l'officielle envoyée par la FFD (importée par le comité) et la même
// carte générée que celle des clubs (cf. LSEF RepLicenses.vue::buildCardEl), au choix.
const downloadingCard = ref<{ id: number; kind: CardKind } | null>(null);

const saveBlob = (blob: Blob, filename: string) => {
  const link = document.createElement('a');
  link.download = filename;
  link.href = URL.createObjectURL(blob);
  link.click();
  URL.revokeObjectURL(link.href);
};

const escapeHtml = (v: string) => v.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const titleCase = (v: string) => v.toLowerCase().replace(/(^|[\s'-])(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase());

function buildCardEl(lic: License): HTMLElement {
  const ligue = lic.ligue.toUpperCase();
  const comite = lic.comite.toUpperCase();
  const card = document.createElement('div');
  card.style.cssText = 'background-color:white;padding:20px;border-radius:10px;width:450px;box-shadow:0 4px 20px rgba(0,0,0,0.2);font-family:Arial,sans-serif;box-sizing:border-box;color:#000;';
  card.innerHTML = `
    <div style="display:flex;align-items:center;border-bottom:2px solid #ccc;padding-bottom:10px;margin-bottom:15px;">
      <img src="${ffdLogo}" style="width:80px;height:auto;margin-right:15px;" />
      <div style="text-align:left;">
        <h1 style="margin:0;font-size:18px;color:#0056b3;">Fédération Française de Darts</h1>
        <p style="margin:5px 0;font-size:14px;">Affiliée à la World Darts Federation</p>
        <p style="margin:5px 0;font-weight:bold;font-size:16px;">Saison: ${escapeHtml(lic.season)}</p>
      </div>
    </div>
    <div>
      <p style="margin:8px 0;text-align:left;font-size:18px;font-weight:bold;text-transform:uppercase;margin-bottom:10px;"><strong>${escapeHtml(lic.name)} ${escapeHtml(lic.firstname)}</strong></p>
      <p style="margin:8px 0;text-align:left;"><strong>Licence N°:</strong> ${lic.license_number}</p>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px 20px;margin-top:15px;font-size:14px;">
        <div><strong>${escapeHtml(titleCase(lic.club_name))}</strong></div>
        <div><strong>Catégorie</strong> ${escapeHtml(formatCategory(lic))}</div>
        <div><strong>Ligue</strong> ${escapeHtml(lic.ligue_name || ligue)} (${escapeHtml(ligue)})</div>
        <div><strong>Comité</strong> ${escapeHtml(lic.comite_name || comite)} (${escapeHtml(comite)})</div>
      </div>
    </div>
    <div style="display:flex;align-items:center;justify-content:flex-end;gap:5px;margin-top:14px;font-size:10px;color:#9ca3af;">
      <img src="${dartshubLogo}" alt="" style="height:12px;width:auto;" />Carte générée par DartsHub
    </div>`;
  return card;
}

async function generateCard(lic: License): Promise<Blob> {
  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position:fixed;left:-9999px;top:0;';
  const card = buildCardEl(lic);
  wrapper.appendChild(card);
  document.body.appendChild(wrapper);
  try {
    await Promise.all(Array.from(card.querySelectorAll('img'))
      .filter(img => !img.complete)
      .map(img => new Promise(res => { img.onload = img.onerror = res; })));
    const canvas = await html2canvas(card, { scale: 1011 / 450, useCORS: true, backgroundColor: '#ffffff', logging: false });
    return await new Promise<Blob>((res, rej) => canvas.toBlob(b => (b ? res(b) : rej(new Error('toBlob'))), 'image/jpeg', 0.95));
  } finally {
    document.body.removeChild(wrapper);
  }
}

// Images prêtes à afficher et à télécharger (même fichier), préparées une fois par licence
// et par type de carte -- clé `${id}-${kind}`.
interface LicenseCardImage { blob: Blob; url: string }
const cardImages = ref<Record<string, LicenseCardImage>>({});
const failedCards = ref<Set<string>>(new Set());
const pendingCards = new Map<string, Promise<LicenseCardImage>>();

function getCardImage(lic: License, kind: CardKind): Promise<LicenseCardImage> {
  const key = `${lic.id}-${kind}`;
  const cached = cardImages.value[key];
  if (cached) return Promise.resolve(cached);
  if (!pendingCards.has(key)) {
    pendingCards.set(key, (async () => {
      const blob = kind === 'ffd'
        ? (await dartsHubApi.get(`/licenses/${lic.id}/ffd-card`, { responseType: 'blob' })).data as Blob
        : await generateCard(lic);
      const image = { blob, url: URL.createObjectURL(blob) };
      cardImages.value = { ...cardImages.value, [key]: image };
      return image;
    })().catch(err => {
      failedCards.value = new Set(failedCards.value).add(key);
      throw err;
    }).finally(() => pendingCards.delete(key)));
  }
  return pendingCards.get(key)!;
}

function loadCards(lic: License) {
  const kinds: CardKind[] = lic.ffd_card_uploaded_at ? ['ffd', 'generated'] : ['generated'];
  kinds.forEach(kind => getCardImage(lic, kind).catch(() => { /* affiché "indisponible" */ }));
}

const cardItems = (lic: License): CardChoiceItem[] => (['ffd', 'generated'] as CardKind[]).map(kind => {
  const key = `${lic.id}-${kind}`;
  const image = cardImages.value[key];
  const state = image ? 'ready'
    : kind === 'ffd' && !lic.ffd_card_uploaded_at ? 'missing'
      : failedCards.value.has(key) ? 'error' : 'loading';
  return { kind, label: kind === 'ffd' ? 'Carte officielle FFD' : 'Carte DartsHub', url: image?.url ?? null, state };
});

const downloadLicenseCard = async (lic: License, kind: CardKind) => {
  downloadingCard.value = { id: lic.id, kind };
  try {
    const card = await getCardImage(lic, kind);
    // "ffd" dans le nom uniquement pour la carte officielle, pas pour la carte générée.
    saveBlob(card.blob, `licence-${kind === 'ffd' ? 'ffd-' : ''}${lic.license_number}-${lic.name}-${lic.firstname}.jpg`);
  } catch {
    toast.error('Carte de licence indisponible.');
  } finally {
    downloadingCard.value = null;
  }
};

const downloadingKind = (lic: License) => (downloadingCard.value?.id === lic.id ? downloadingCard.value.kind : null);

watch(lastLicense, lic => {
  if (lic) loadCards(lic);
});

onBeforeUnmount(() => Object.values(cardImages.value).forEach(c => URL.revokeObjectURL(c.url)));

const loadLicenses = async () => {
  loadingLicenses.value = true;
  try {
    const { data } = await dartsHubApi.get('/licenses/me');
    licenses.value = data;
  } finally {
    loadingLicenses.value = false;
  }
};

const loadSessions = async () => {
  loadingSessions.value = true;
  try {
    const { data } = await dartsHubApi.get('/auth/my-sessions');
    sessions.value = data;
  } finally {
    loadingSessions.value = false;
  }
};

const revokeSession = async (session: Session) => {
  const ok = await openConfirm(
    'Déconnecter cette session ?',
    `Vous serez déconnecté(e) de ${siteLabel(session.app_name)} (${deviceLabel(session.user_agent)}) à la prochaine action sur ce site.`,
  );
  if (!ok) return;
  revokingId.value = session.id;
  try {
    await dartsHubApi.post(`/auth/sessions/${session.id}/revoke`);
    sessions.value = sessions.value.filter((s) => s.id !== session.id);
    toast.success('Session déconnectée.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la déconnexion de la session.');
  } finally {
    revokingId.value = null;
  }
};

const revokeAllSessions = async () => {
  const ok = await openConfirm(
    'Tout déconnecter ?',
    `Les ${sessions.value.length} connexions actives seront révoquées, y compris celle-ci : vous devrez vous reconnecter.`,
  );
  if (!ok) return;
  revokingAll.value = true;
  try {
    await dartsHubApi.post('/auth/sessions/revoke-all');
    toast.success('Toutes les sessions ont été déconnectées.');
    logout();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la déconnexion générale.');
    revokingAll.value = false;
  }
};

const saveProfile = async () => {
  saving.value = true;
  try {
    const { data } = await dartsHubApi.patch('/users/me', {
      name: form.value.name,
      firstname: form.value.firstname,
      nickname: form.value.nickname,
      bio: form.value.bio,
      city: form.value.city,
    });
    authStore.setAuthData({
      token: authStore.token,
      email: data.email,
      name: data.name,
      firstname: data.firstname,
      nickname: data.nickname || '',
      profile_picture: data.profile_picture || '',
      role: data.role,
      scopes: authStore.scopes,
    });
    editMode.value = false;
    toast.success('Profil mis à jour.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde.');
  } finally {
    saving.value = false;
  }
};

const changePassword = async () => {
  if (passwordForm.value.next !== passwordForm.value.confirm) {
    toast.error('Les mots de passe ne correspondent pas.');
    return;
  }
  saving.value = true;
  try {
    await dartsHubApi.patch('/users/me', { password: passwordForm.value.next });
    toast.success('Mot de passe modifié.');
    passwordMode.value = false;
    passwordForm.value = { next: '', confirm: '' };
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    saving.value = false;
  }
};

// ── Prochains tournois + classements ────────────────────────────────────────
interface UpcomingEvent {
  id: number;
  name: string;
  date: string;
  place: string | null;
}

const upcomingEvents = ref<UpcomingEvent[]>([]);
const loadingUpcoming = ref(true);

interface RankingEntry {
  category: string;
  clt: string | number;
  pts: string | number;
}

const lsefRankings = ref<RankingEntry[]>([]);
const cmerRankings = ref<RankingEntry[]>([]);
const ffdRankings = ref<RankingEntry[]>([]);
const loadingRankings = ref(true);

const formatRankingCategory = (cat: string) =>
  cat.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const formatEventDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Les classements CMER/LSEF/FFD sont repris de documents PDF officiels : pas de vraie
// clé de jointure avec le compte, juste le nom du joueur affiché tel quel dans le
// document -- même heuristique de rapprochement que CMER/Badarts (nickname en priorité,
// sinon prénom+nom retrouvés dans le texte).
const normalizeForMatch = (str: string): string => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/[^a-zA-Z\s-]/g, '')
    .toLowerCase()
    .trim();
};

const isCurrentUserEntry = (joueur: string): boolean => {
  const normalizedPlayer = normalizeForMatch(joueur);
  if (!normalizedPlayer) return false;
  if (authStore.nickname && normalizeForMatch(authStore.nickname) === normalizedPlayer) return true;
  const normFirst = normalizeForMatch(authStore.firstname || '');
  const normLast = normalizeForMatch(authStore.name || '');
  if (normFirst && normLast) return normalizedPlayer.includes(normFirst) && normalizedPlayer.includes(normLast);
  if (normLast) return normalizedPlayer.includes(normLast);
  if (normFirst) return normalizedPlayer.includes(normFirst);
  return false;
};

const extractUserRankings = (leaderboard: any[]): RankingEntry[] => {
  const entries: RankingEntry[] = [];
  for (const cat of leaderboard || []) {
    const entry = (cat.entries || []).find((e: any) => isCurrentUserEntry(e.joueur));
    if (entry) entries.push({ category: cat.category, clt: entry.clt, pts: entry.pts });
  }
  return entries;
};

const loadUpcomingEvents = async () => {
  loadingUpcoming.value = true;
  try {
    const [{ data: myInscriptions }, { data: events }] = await Promise.all([
      dartsHubApi.get('/inscriptions/my-registrations'),
      dartsHubApi.get('/events/'),
    ]);
    const now = new Date();
    const myEventIds = new Set<number>(myInscriptions.map((ins: any) => ins.event_id));
    upcomingEvents.value = events
      .filter((e: any) => myEventIds.has(e.id))
      // Nom affiché seulement : nom complet ("Open de Comité #2 (MER)", "Coupe du Comité Réunion").
      .map((e: any) => ({ id: e.id, name: e.full_name || e.display_name || e.name, date: e.event_date, place: e.location ?? null }))
      .filter((e: UpcomingEvent) => {
        const d = new Date(e.date);
        return !Number.isNaN(d.getTime()) && d >= now;
      })
      .sort((a: UpcomingEvent, b: UpcomingEvent) => new Date(a.date).getTime() - new Date(b.date).getTime());
  } catch {
    upcomingEvents.value = [];
  } finally {
    loadingUpcoming.value = false;
  }
};

const loadRankings = async () => {
  loadingRankings.value = true;
  const season = getCurrentSeason();
  try {
    const [lsef, cmer, ffd] = await Promise.all([
      dartsHubApi.get(`/leaderboard/lsef/season/${season}`),
      dartsHubApi.get(`/leaderboard/cmer/season/${season}`),
      dartsHubApi.get(`/leaderboard/ffd/season/${season}`),
    ]);
    lsefRankings.value = extractUserRankings(lsef.data.leaderboard);
    cmerRankings.value = extractUserRankings(cmer.data.leaderboard);
    ffdRankings.value = extractUserRankings(ffd.data.leaderboard);
  } catch {
    lsefRankings.value = [];
    cmerRankings.value = [];
    ffdRankings.value = [];
  } finally {
    loadingRankings.value = false;
  }
};

const uploadPhoto = async (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  uploading.value = true;
  const formData = new FormData();
  formData.append('file', file);
  try {
    const { data } = await dartsHubApi.post(`/users/${authStore.userId}/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    authStore.setAuthData({
      token: authStore.token,
      email: authStore.email,
      name: authStore.name,
      firstname: authStore.firstname,
      nickname: authStore.nickname,
      profile_picture: data.url,
      role: authStore.role,
      scopes: authStore.scopes,
    });
    toast.success('Photo mise à jour.');
  } catch {
    toast.error("Erreur lors de l'upload.");
  } finally {
    uploading.value = false;
  }
};

const logout = () => {
  authStore.logout();
  router.push('/login');
};

onMounted(() => {
  loadProfile();
  loadLicenses();
  loadSessions();
  loadConsents();
  loadUpcomingEvents();
  loadRankings();
});
</script>

<template>
  <div class="profile-page">

    <!-- ── Hero ─────────────────────────────────────────────────────────────── -->
    <div class="profile-hero">
      <svg class="hero-target" viewBox="-140 -140 280 280" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="0" cy="0" r="140" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.3)" stroke-width="1.2" />
        <circle cx="0" cy="0" r="82" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.3)" stroke-width="1" />
        <circle cx="0" cy="0" r="16" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.2" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.15)" stroke-width="0.8"
          transform="rotate(0)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.15)" stroke-width="0.8"
          transform="rotate(36)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.15)" stroke-width="0.8"
          transform="rotate(72)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.15)" stroke-width="0.8"
          transform="rotate(108)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.15)" stroke-width="0.8"
          transform="rotate(144)" />
      </svg>

      <div class="hero-content">
        <div class="hero-avatar-wrap">
          <img v-if="authStore.profilePictureUrl" :src="authStore.profilePictureUrl" class="hero-avatar" alt="avatar" />
          <div v-else class="hero-avatar hero-avatar-placeholder">{{ initials }}</div>
          <label class="hero-avatar-upload" :class="{ loading: uploading }" title="Changer la photo">
            <i class="fa-solid fa-camera" />
            <input type="file" accept="image/jpeg,image/png" hidden @change="uploadPhoto" />
          </label>
        </div>

        <div class="hero-info">
          <div class="hero-name">{{ authStore.firstname }} {{ authStore.name }}</div>
          <div class="hero-meta">
            <span v-if="authStore.nickname" class="hero-nickname">@{{ authStore.nickname }}</span>
            <span v-if="form.city" class="hero-city"><i class="fa-solid fa-location-dot" /> {{ form.city }}</span>
          </div>
          <div class="hero-tricolor"><span></span><span></span><span></span></div>
        </div>
      </div>
    </div>

    <!-- ── Corps ─────────────────────────────────────────────────────────────── -->
    <div class="profile-body">

      <!-- Colonne 1 -->
      <div class="profile-col">

        <!-- Profil -->
        <div class="pcard">
          <div class="pcard-header">
            <i class="fa-solid fa-user" />
            <span>Profil</span>
            <button class="pcard-action" @click="editMode = !editMode; passwordMode = false">
              <i :class="editMode ? 'fa-solid fa-xmark' : 'fa-solid fa-pen'" />
              {{ editMode ? 'Annuler' : 'Modifier' }}
            </button>
          </div>

          <div v-if="!editMode" class="pcard-body">
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Prénom</span>
                <span class="info-value">{{ form.firstname || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Nom</span>
                <span class="info-value">{{ form.name || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Pseudo</span>
                <span class="info-value">{{ form.nickname || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Email</span>
                <span class="info-value">{{ authStore.email }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Ville</span>
                <span class="info-value">{{ form.city || '-' }}</span>
              </div>
              <div v-if="form.bio" class="info-item info-item-full">
                <span class="info-label">Bio</span>
                <span class="info-value info-bio">{{ form.bio }}</span>
              </div>
            </div>
          </div>

          <form v-else class="pcard-body pform" @submit.prevent="saveProfile">
            <div class="pform-row">
              <div class="pfield">
                <label>Prénom</label>
                <input v-model="form.firstname" type="text" class="pinput" />
              </div>
              <div class="pfield">
                <label>Nom</label>
                <input v-model="form.name" type="text" class="pinput" />
              </div>
            </div>
            <p class="pform-hint">
              <i class="fa-solid fa-circle-info" /> Indiquez votre nom tel qu'il figure sur votre licence FFD, pas un
              pseudo. Changer de nom ne relie pas de licence à votre compte : si la vôtre n'y apparaît pas,
              contactez votre club.
            </p>
            <div class="pform-row">
              <div class="pfield">
                <label>Pseudo</label>
                <input v-model="form.nickname" type="text" class="pinput" />
              </div>
              <div class="pfield">
                <label>Ville</label>
                <input v-model="form.city" type="text" class="pinput" placeholder="ex: Montpellier" />
              </div>
            </div>
            <div class="pfield">
              <label>Bio</label>
              <textarea v-model="form.bio" class="pinput ptextarea" rows="3" placeholder="Quelques mots sur vous…" />
            </div>
            <button type="submit" class="pbtn pbtn-primary" :disabled="saving">
              <i v-if="saving" class="fa-solid fa-spinner fa-spin" />
              {{ saving ? 'Sauvegarde…' : 'Sauvegarder' }}
            </button>
          </form>
        </div>

        <!-- Mot de passe -->
        <div class="pcard">
          <div class="pcard-header">
            <i class="fa-solid fa-lock" />
            <span>Mot de passe</span>
            <button class="pcard-action" @click="passwordMode = !passwordMode; editMode = false">
              <i :class="passwordMode ? 'fa-solid fa-xmark' : 'fa-solid fa-key'" />
              {{ passwordMode ? 'Annuler' : 'Changer' }}
            </button>
          </div>

          <div v-if="!passwordMode" class="pcard-body">
            <p class="pcard-hint">Votre mot de passe est sécurisé. Changez-le si nécessaire.</p>
          </div>

          <form v-else class="pcard-body pform" @submit.prevent="changePassword">
            <div class="pfield">
              <label>Nouveau mot de passe</label>
              <div class="ppw-wrap">
                <input v-model="passwordForm.next" :type="showPwNext ? 'text' : 'password'" minlength="8" required
                  class="pinput ppw-input" />
                <button type="button" class="ppw-toggle" tabindex="-1" @click="showPwNext = !showPwNext">
                  <i :class="showPwNext ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" />
                </button>
              </div>
            </div>
            <div class="pfield">
              <label>Confirmer</label>
              <div class="ppw-wrap">
                <input v-model="passwordForm.confirm" :type="showPwConfirm ? 'text' : 'password'" required
                  class="pinput ppw-input" />
                <button type="button" class="ppw-toggle" tabindex="-1" @click="showPwConfirm = !showPwConfirm">
                  <i :class="showPwConfirm ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" />
                </button>
              </div>
            </div>
            <button type="submit" class="pbtn pbtn-primary" :disabled="saving">
              <i v-if="saving" class="fa-solid fa-spinner fa-spin" />
              {{ saving ? 'Modification…' : 'Modifier' }}
            </button>
          </form>
        </div>

        <!-- Apparence -->
        <div class="pcard">
          <div class="pcard-header">
            <i class="fa-solid fa-palette" />
            <span>Apparence</span>
          </div>
          <div style="padding: 18px 20px; display: grid; grid-template-columns: 1fr 1fr; gap: 14px;">
            <button v-for="t in THEMES" :class="{ 'theme-card': true, active: themeStore.currentTheme === t.id }"
              :title="t.description"
              style="position:relative;display:flex;flex-direction:column;align-items:center;gap:8px;padding:14px 10px 12px;border:2px solid var(--border);border-radius:10px;background:var(--bg-input);cursor:pointer;font-family:inherit;transition:border-color 0.2s;width:100%;box-sizing:border-box;"
              @click="themeStore.setTheme(t.id as Theme)" :key="t.id">
              <div
                :style="{ background: t.bg, width: '100%', height: '32px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }">
                <div :style="{ background: t.primary, width: '10px', height: '10px', borderRadius: '50%' }" />
                <div :style="{ background: t.accent, width: '10px', height: '10px', borderRadius: '50%' }" />
              </div>
              <span style="font-size:0.7rem;font-weight:600;color:var(--text-muted);">{{ t.label }}</span>
              <i v-if="themeStore.currentTheme === t.id" class="fa-solid fa-check"
                style="position:absolute;top:6px;right:8px;font-size:0.6rem;color:var(--primary);" />
            </button>
          </div>
        </div>

        <!-- Déconnexion -->
        <button class="pbtn pbtn-logout" @click="logout">
          <i class="fa-solid fa-right-from-bracket" />
          Se déconnecter
        </button>

        <!-- Redirection -->
        <button v-if="redirectTarget" class="pbtn pbtn-redirect" @click="goToRedirect">
          <i class="fa-solid fa-arrow-left" /> Retour{{ redirectDomain ? ` à ${redirectDomain}` : '' }}
        </button>
        <RouterLink v-else to="/" class="pbtn pbtn-redirect">
          <i class="fa-solid fa-arrow-left" /> Accueil
        </RouterLink>

      </div>

      <!-- Colonne 2 -->
      <div class="profile-col">

        <!-- Licence -->
        <div class="pcard">
          <div class="pcard-header">
            <i class="fa-solid fa-id-card" />
            <span>Licence</span>
          </div>
          <div class="pcard-body">
            <div v-if="loadingLicenses" class="pcard-loading">
              <i class="fa-solid fa-spinner fa-spin" /> Chargement…
            </div>
            <div v-else-if="!licenses.length" class="pcard-empty">
              <i class="fa-solid fa-id-card-clip" />
              <p>Aucune licence enregistrée</p>
            </div>
            <template v-else>
              <div v-if="lastLicense" class="license-main">
                <div class="license-header-row">
                  <div class="license-season">Saison {{ lastLicense.season }}</div>
                  <span v-if="isCurrentLicenseValid" class="badge-valid"><i class="fa-solid fa-circle-check" />
                    Valide</span>
                  <span v-else class="badge-expired"><i class="fa-solid fa-circle-xmark" /> Expirée</span>
                </div>
                <div class="info-grid">
                  <div class="info-item">
                    <span class="info-label">N° licence</span>
                    <span class="info-value">{{ lastLicense.license_number }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Catégorie</span>
                    <span class="info-value">{{ formatCategory(lastLicense) }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Club</span>
                    <span class="info-value" style="text-transform: capitalize">{{ lastLicense.club_name }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">N° club</span>
                    <span class="info-value">{{ lastLicense.club_number }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Ligue</span>
                    <span class="info-value">{{ lastLicense.ligue }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Comité</span>
                    <span class="info-value">{{ lastLicense.comite }}</span>
                  </div>
                </div>
                <LicenseCardChoice :items="cardItems(lastLicense)" :downloading="downloadingKind(lastLicense)"
                  @download="kind => downloadLicenseCard(lastLicense!, kind)" />
              </div>
              <div v-if="licenses.length > 1" class="license-history">
                <div class="pcard-subheader">Historique</div>
                <div v-for="lic in sortedLicenses.slice(1)"
                  class="license-history-row" :key="lic.id">
                  <span class="info-label">{{ lic.season }}</span>
                  <span class="license-history-club">{{ lic.club_name }}</span>
                  <span class="license-history-meta">
                    <span class="license-history-number">N° {{ lic.license_number }}</span>
                    <span v-if="formatCategory(lic)" class="cat-badge">{{ formatCategory(lic) }}</span>
                  </span>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Prochains tournois -->
        <div class="pcard">
          <div class="pcard-header">
            <i class="fa-solid fa-calendar-days" />
            <span>Mes prochains tournois</span>
          </div>
          <div class="pcard-body">
            <div v-if="loadingUpcoming" class="pcard-loading">
              <i class="fa-solid fa-spinner fa-spin" /> Chargement…
            </div>
            <div v-else-if="!upcomingEvents.length" class="pcard-empty">
              <i class="fa-solid fa-calendar-xmark" />
              <p>Aucun tournoi officiel à venir</p>
            </div>
            <div v-else class="upcoming-list">
              <div v-for="ev in upcomingEvents" class="upcoming-item" :key="ev.id">
                <div class="session-site">{{ ev.name }}</div>
                <div class="session-meta">
                  {{ formatEventDate(ev.date) }}<span v-if="ev.place"> · {{ ev.place }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Classements -->
        <div class="pcard">
          <div class="pcard-header">
            <i class="fa-solid fa-trophy" />
            <span>Mes classements {{ getCurrentSeason() }}</span>
          </div>
          <div class="pcard-body">
            <div v-if="loadingRankings" class="pcard-loading">
              <i class="fa-solid fa-spinner fa-spin" /> Chargement…
            </div>
            <template v-else>
              <div class="pcard-subheader">Ligue Sud-Est</div>
              <div v-if="lsefRankings.length" class="ranking-list">
                <div v-for="r in lsefRankings" class="ranking-item" :key="`lsef-${r.category}`">
                  <span class="cat-badge">{{ formatRankingCategory(r.category) }}</span>
                  <span class="info-value">{{ r.clt }} <small>({{ r.pts }} pts)</small></span>
                </div>
              </div>
              <p v-else class="pcard-empty-inline">Aucun résultat LSEF</p>

              <div class="pcard-subheader">Comité Méridional</div>
              <div v-if="cmerRankings.length" class="ranking-list">
                <div v-for="r in cmerRankings" class="ranking-item" :key="`cmer-${r.category}`">
                  <span class="cat-badge">{{ formatRankingCategory(r.category) }}</span>
                  <span class="info-value">{{ r.clt }} <small>({{ r.pts }} pts)</small></span>
                </div>
              </div>
              <p v-else class="pcard-empty-inline">Aucun résultat CMER</p>

              <div class="pcard-subheader">Fédéral FFD</div>
              <div v-if="ffdRankings.length" class="ranking-list">
                <div v-for="r in ffdRankings" class="ranking-item" :key="`ffd-${r.category}`">
                  <span class="cat-badge">{{ formatRankingCategory(r.category) }}</span>
                  <span class="info-value">{{ r.clt }} <small>({{ r.pts }} pts)</small></span>
                </div>
              </div>
              <p v-else class="pcard-empty-inline">Aucun résultat FFD</p>
            </template>
          </div>
        </div>

      </div>

      <!-- Colonne 3 -->
      <div class="profile-col">

        <!-- Applications connectées -->
        <div class="pcard">
          <div class="pcard-header">
            <i class="fa-solid fa-plug" />
            <span>Applications connectées</span>
          </div>
          <div class="pcard-body">
            <div v-if="loadingConsents || loadingSessions" class="pcard-loading">
              <i class="fa-solid fa-spinner fa-spin" /> Chargement…
            </div>
            <div v-else-if="!connectedApps.length" class="pcard-empty">
              <i class="fa-solid fa-plug" />
              <p>Aucune application connectée</p>
            </div>
            <div v-else class="session-list">
              <div v-for="app in connectedApps" class="session-row" :key="app.key">
                <div class="session-info">
                  <div class="session-site">{{ app.name }}</div>
                  <div v-if="app.type === 'session'" class="session-meta">
                    {{ app.count }} appareil{{ app.count > 1 ? 's' : '' }} · dernière connexion {{ formatSessionDate(app.lastDate) }}
                  </div>
                  <div v-else class="session-meta">
                    Autorisé le {{ formatSessionDate(app.lastDate) }} · ne coupe pas une session déjà ouverte sur ce site
                  </div>
                </div>
                <button v-if="app.type === 'session'" class="session-revoke" :disabled="revokingAppKey === app.key"
                  title="Déconnecter cette application" @click="disconnectApp(app)">
                  <i v-if="revokingAppKey === app.key" class="fa-solid fa-spinner fa-spin" />
                  <i v-else class="fa-solid fa-right-from-bracket" />
                </button>
                <button v-else class="session-revoke" :disabled="revokingConsentId === app.consent!.client_id"
                  title="Révoquer cette autorisation" @click="revokeConsent(app.consent!)">
                  <i v-if="revokingConsentId === app.consent!.client_id" class="fa-solid fa-spinner fa-spin" />
                  <i v-else class="fa-solid fa-xmark" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Connexions -->
        <div class="pcard">
          <div class="pcard-header">
            <i class="fa-solid fa-shield-halved" />
            <span>Appareils connectés{{ sessions.length ? ` (${sessions.length})` : '' }}</span>
            <button v-if="sessions.length > 1" class="pcard-action" :disabled="revokingAll" @click="revokeAllSessions">
              <i v-if="revokingAll" class="fa-solid fa-spinner fa-spin" />
              <i v-else class="fa-solid fa-power-off" />
              Tout déconnecter
            </button>
          </div>
          <div class="pcard-body">
            <div v-if="loadingSessions" class="pcard-loading">
              <i class="fa-solid fa-spinner fa-spin" /> Chargement…
            </div>
            <div v-else-if="!sessions.length" class="pcard-empty">
              <i class="fa-solid fa-shield-halved" />
              <p>Aucune connexion récente à afficher</p>
            </div>
            <template v-else>
              <div class="session-list">
                <div v-for="s in visibleSessions" class="session-row" :key="s.id">
                  <div class="session-info">
                    <div class="session-site">{{ siteLabel(s.app_name) }}</div>
                    <div class="session-meta">{{ deviceLabel(s.user_agent) }} · {{ formatSessionDate(s.created_at) }}</div>
                  </div>
                  <button class="session-revoke" :disabled="revokingId === s.id" title="Déconnecter cette session" @click="revokeSession(s)">
                    <i v-if="revokingId === s.id" class="fa-solid fa-spinner fa-spin" />
                    <i v-else class="fa-solid fa-right-from-bracket" />
                  </button>
                </div>
              </div>
              <button v-if="sessions.length > SESSIONS_PREVIEW_COUNT" class="pcard-action" style="margin-top: 10px"
                @click="showAllSessions = !showAllSessions">
                {{ showAllSessions ? 'Réduire' : `Voir les ${sessions.length - SESSIONS_PREVIEW_COUNT} autres` }}
              </button>
            </template>
          </div>
        </div>

      </div>
    </div>

    <!-- ── Footer ── -->
    <Footer />

  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

* {
  box-sizing: border-box;
}

.profile-page {
  min-height: 100svh;
  background: var(--bg);
  font-family: 'Poppins', 'Segoe UI', sans-serif;
  color: var(--text);
  display: flex;
  flex-direction: column;
}

/* ── Hero ── */
.profile-hero {
  background: linear-gradient(145deg, var(--dh-header-from) 0%, var(--primary) 60%, var(--dh-header-to) 100%);
  position: relative;
  overflow: hidden;
  padding: 36px 40px;
}

.hero-target {
  position: absolute;
  top: 50%;
  right: -40px;
  transform: translateY(-50%);
  width: 300px;
  height: 300px;
  pointer-events: none;
  opacity: 0.5;
}

.hero-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 28px;
  max-width: 1320px;
  margin: 0 auto;
  width: 100%;
}

.hero-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.hero-avatar {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-avatar-placeholder {
  background: rgba(255, 255, 255, 0.15);
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
}

.hero-avatar-upload {
  position: absolute;
  bottom: 2px;
  right: 2px;
  background: var(--primary);
  color: #fff;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.7rem;
  border: 2px solid rgba(255, 255, 255, 0.6);
}

.hero-avatar-upload:hover {
  background: var(--primary-hover);
}

.hero-info {
  flex: 1;
}

.hero-name {
  font-size: clamp(1.3rem, 3vw, 1.8rem);
  font-weight: 700;
  color: #fff;
  letter-spacing: 1px;
}

.hero-meta {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 4px 0 10px;
}

.hero-nickname {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.hero-city {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 4px;
}

.hero-tricolor {
  display: flex;
  height: 4px;
  width: 80px;
  border-radius: 10px;
  overflow: hidden;
  gap: 3px;
  margin-bottom: 10px;
}

.hero-tricolor span {
  flex: 1;
  border-radius: 10px;
}

.hero-tricolor span:nth-child(1) {
  background: var(--dh-bar-1);
}

.hero-tricolor span:nth-child(2) {
  background: var(--dh-bar-2);
}

.hero-tricolor span:nth-child(3) {
  background: var(--dh-bar-3);
}

/* ── Body ── */
.profile-body {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px 28px;
  /* 20px horizontal • 28px vertical */
  padding: 28px 40px;
  max-width: 1320px;
  margin: 0 auto;
  width: 100%;
  flex: 1;
  margin-bottom: 20px;
}

/* ── Cards ── */
.pcard {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  margin-bottom: 20px;
}

.pcard-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--border);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.pcard-header i {
  color: var(--primary);
  font-size: 0.9rem;
}

.pcard-action {
  margin-left: auto;
  background: none;
  border: none;
  color: var(--primary);
  font-weight: 600;
  font-size: 0.78rem;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0;
}

.pcard-action:hover {
  text-decoration: underline;
}

.pcard-body {
  padding: 18px 20px;
}

.pcard-hint {
  color: var(--text-muted);
  font-size: 0.82rem;
  margin: 0;
}

.pcard-loading,
.pcard-empty {
  text-align: center;
  color: var(--text-muted);
  font-size: 0.85rem;
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.pcard-empty i {
  font-size: 1.8rem;
  opacity: 0.4;
}

.pcard-subheader {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 14px 0 8px;
}

/* ── Info grid ── */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.info-item-full {
  grid-column: 1 / -1;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.info-value {
  font-size: 0.88rem;
}

.info-bio {
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.5;
}

/* ── Form ── */
.pform {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pform-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: -4px 0 0;
  color: var(--text-muted);
  font-size: 0.78rem;
  line-height: 1.4;
}

.pform-hint i {
  color: var(--primary);
  margin-top: 2px;
  flex-shrink: 0;
}

.pform-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.pfield {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pfield label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
}

.pinput {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid var(--border);
  border-radius: 8px;
  background: var(--bg-input);
  color: var(--text);
  font-size: 0.87rem;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.pinput:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 10%, transparent);
}

.ptextarea {
  resize: vertical;
  min-height: 70px;
}

.ppw-wrap {
  position: relative;
}

.ppw-input {
  padding-right: 2.6rem;
}

.ppw-toggle {
  position: absolute;
  right: 0.7rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0;
  font-size: 0.88rem;
  display: flex;
  align-items: center;
}

.ppw-toggle:hover {
  color: var(--text);
}

/* ── Buttons ── */
.pbtn {
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  font-size: 0.87rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.pbtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pbtn-primary {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%);
  color: #fff;
  box-shadow: 0 3px 12px color-mix(in srgb, var(--primary) 25%, transparent);
}

.pbtn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
}

.pbtn-logout {
  background: color-mix(in srgb, var(--danger) 8%, transparent);
  color: var(--danger);
  border: 1.5px solid color-mix(in srgb, var(--danger) 25%, transparent);
  font-size: 0.85rem;
  margin-top: 4px;
}

.pbtn-logout:hover {
  background: color-mix(in srgb, var(--danger) 15%, transparent);
  border-color: var(--danger);
}

.pbtn-redirect {
  background: color-mix(in srgb, var(--success) 8%, transparent);
  color: var(--success);
  border: 1.5px solid color-mix(in srgb, var(--success) 25%, transparent);
  font-size: 0.85rem;
  margin-top: 4px;
  text-decoration: none;
}

.pbtn-redirect:hover {
  background: color-mix(in srgb, var(--success) 15%, transparent);
  border-color: var(--success);
}










/* ── Licence ── */
.license-main {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.license-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.license-season {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--primary);
}

.license-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.license-detail {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.badge-valid {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: color-mix(in srgb, var(--success) 12%, transparent);
  color: var(--success);
  font-size: 0.78rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid color-mix(in srgb, var(--success) 25%, transparent);
  width: fit-content;
}

.badge-expired {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: color-mix(in srgb, var(--danger) 12%, transparent);
  color: var(--danger);
  font-size: 0.78rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid color-mix(in srgb, var(--danger) 25%, transparent);
  width: fit-content;
}

.license-history {
  border-top: 1px solid var(--border);
  margin-top: 12px;
}

/* Deux lignes par licence : saison + club en entier, puis n° et catégorie alignés sous le
   club (colonne de saison fixe, pour que tout reste aligné d'une licence à l'autre). */
.license-history-row {
  display: grid;
  grid-template-columns: 5.5rem minmax(0, 1fr);
  column-gap: 10px;
  row-gap: 3px;
  align-items: baseline;
  font-size: 0.82rem;
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
}

.license-history-club {
  text-transform: capitalize;
  overflow-wrap: anywhere;
}

.license-history-meta {
  grid-column: 2;
  display: flex;
  align-items: center;
  gap: 8px;
}

.license-history-number {
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

.license-history-meta .cat-badge {
  margin-left: 0;
}

.license-history-row:last-child {
  border-bottom: none;
}

.cat-badge {
  margin-left: auto;
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 600;
}

/* ── Connexions ── */
.session-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.session-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
}

.session-row:last-child {
  border-bottom: none;
}

.session-site {
  font-size: 0.87rem;
  font-weight: 600;
}

.session-meta {
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* ── Prochains tournois ── */
.upcoming-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.upcoming-item {
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
}

.upcoming-item:last-child {
  border-bottom: none;
}

/* ── Classements ── */
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ranking-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
}

.ranking-item:last-child {
  border-bottom: none;
}

.ranking-item .cat-badge {
  margin-left: 0;
}

.pcard-empty-inline {
  color: var(--text-muted);
  font-size: 0.8rem;
  margin: 0 0 4px;
}

.session-revoke {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1.5px solid color-mix(in srgb, var(--danger) 25%, transparent);
  background: color-mix(in srgb, var(--danger) 8%, transparent);
  color: var(--danger);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.session-revoke:hover:not(:disabled) {
  background: color-mix(in srgb, var(--danger) 15%, transparent);
}

.session-revoke:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Sites connectés ── */
.site-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  text-decoration: none;
  color: var(--text);
  border-bottom: 1px solid var(--border);
  transition: color 0.15s;
}

.site-link:last-child {
  border-bottom: none;
}

.site-link:hover {
  color: var(--primary);
}

.site-link-info {
  flex: 1;
}

.site-link-name {
  display: block;
  font-weight: 600;
  font-size: 0.88rem;
}

.site-link-desc {
  display: block;
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 2px;
}

.site-link i {
  color: var(--text-muted);
  font-size: 0.75rem;
  flex-shrink: 0;
}

.site-link:hover i {
  color: var(--primary);
}

/* ── Themes ── */
.theme-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.theme-wrap {
  display: block;
}

.theme-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 14px 10px 12px;
  border: 2px solid var(--border);
  border-radius: 10px;
  background: var(--bg-input);
  cursor: pointer;
  transition: border-color 0.2s, transform 0.15s;
  font-family: inherit;
  box-sizing: border-box;
}

.theme-card:hover {
  transform: translateY(-1px);
}

.theme-card.active {
  border-color: var(--primary);
}

.theme-preview {
  width: 100%;
  height: 30px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.theme-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.theme-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-muted);
}

.theme-check {
  position: absolute;
  top: 6px;
  right: 8px;
  font-size: 0.6rem;
  color: var(--primary);
}

/* ── Footer ── */

/* ── Responsive ── */
@media (max-width: 1100px) {
  .profile-body {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 700px) {
  .profile-hero {
    padding: 24px 20px;
  }

  .hero-content {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }

  .hero-target {
    display: none;
  }

  .profile-body {
    grid-template-columns: 1fr;
    padding: 20px;
  }

  .pform-row {
    grid-template-columns: 1fr;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

}
</style>
